/**
 * Summary Cards Component
 *
 * Displays key simulation metrics in card format with optional trend indicators.
 * Includes contextual descriptions and accessibility improvements.
 */

import { useMemo, useState } from 'react';
import type { ESOData } from '@/simulation/parsers/types';
import type { SummaryMetric } from '../utils/metrics';
import { generateSummaryMetrics } from '../utils/metrics';
import type { UnitSystem } from '../utils/unitConversion';
import { convertValue, getDisplayUnit, formatNumber } from '../utils/unitConversion';

/**
 * Contextual descriptions for each metric
 */
const METRIC_DESCRIPTIONS: Record<string, { description: string; benchmark?: string }> = {
  'Total Site Energy': {
    description: 'Total energy consumed by the building over the simulation period.',
    benchmark: 'Lower is better. Compare against similar buildings or previous designs.',
  },
  'Energy Use Intensity': {
    description: 'Energy consumption normalized by floor area (kWh/m² or kBtu/ft²).',
    benchmark: 'ASHRAE 90.1 target: ~100-150 kWh/m² for commercial buildings.',
  },
  'Peak Electric Demand': {
    description: 'Maximum instantaneous electricity draw. Affects utility costs.',
    benchmark: 'Lower peak demand reduces demand charges on utility bills.',
  },
  'Unmet Hours': {
    description: 'Hours when zone temperatures were outside setpoint range.',
    benchmark: 'ASHRAE 90.1 limit: 300 hours (heating + cooling combined).',
  },
};

/**
 * Props for a single summary card
 */
interface SummaryCardProps {
  metric: SummaryMetric;
  unitSystem: UnitSystem;
}

/**
 * Icon components for different metric categories
 */
function EnergyIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 10V3L4 14h7v7l9-11h-7z"
      />
    </svg>
  );
}

function DemandIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  );
}

function ComfortIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function DefaultIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    </svg>
  );
}

/**
 * Get icon component for a metric category
 */
function getIcon(category: SummaryMetric['category']) {
  switch (category) {
    case 'energy':
      return <EnergyIcon />;
    case 'demand':
      return <DemandIcon />;
    case 'comfort':
      return <ComfortIcon />;
    default:
      return <DefaultIcon />;
  }
}

/**
 * Get background color class for a metric category
 */
function getCategoryColor(category: SummaryMetric['category']): string {
  switch (category) {
    case 'energy':
      return 'bg-amber-500/20 text-amber-400';
    case 'demand':
      return 'bg-blue-500/20 text-blue-400';
    case 'comfort':
      return 'bg-green-500/20 text-green-400';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

/**
 * Single summary card component with contextual information
 */
function SummaryCard({ metric, unitSystem }: SummaryCardProps) {
  const [showInfo, setShowInfo] = useState(false);

  // Get contextual description
  const context = METRIC_DESCRIPTIONS[metric.title];

  // Convert value if needed
  const displayValue = useMemo(() => {
    if (unitSystem === 'SI') {
      return metric.displayValue;
    }

    // Convert to IP units
    const converted = convertValue(metric.value, metric.unit, unitSystem);
    const displayUnit = getDisplayUnit(metric.unit, unitSystem);

    // Format based on magnitude
    let formatted: string;
    if (Math.abs(converted) >= 1000000) {
      formatted = (converted / 1000000).toFixed(1) + 'M';
    } else if (Math.abs(converted) >= 1000) {
      formatted = (converted / 1000).toFixed(1) + 'k';
    } else {
      formatted = formatNumber(converted, 1);
    }

    return `${formatted} ${displayUnit}`;
  }, [metric, unitSystem]);

  // Determine if this metric is within typical range (for visual feedback)
  const getStatusIndicator = () => {
    if (metric.title === 'Unmet Hours') {
      // ASHRAE 90.1 limit is 300 hours
      if (metric.value <= 50) return { status: 'good', label: 'Excellent' };
      if (metric.value <= 300) return { status: 'ok', label: 'Acceptable' };
      return { status: 'warning', label: 'Exceeds limit' };
    }
    return null;
  };

  const status = getStatusIndicator();

  return (
    <div
      className="bg-card rounded-lg p-4 border border-border hover:border-muted-foreground transition-colors relative group"
      role="article"
      aria-label={`${metric.title}: ${displayValue}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-muted-foreground truncate">{metric.title}</p>
            {context && (
              <button
                onClick={() => setShowInfo(!showInfo)}
                className="p-0.5 text-muted-foreground/60 hover:text-muted-foreground transition-colors shrink-0"
                aria-label={`More info about ${metric.title}`}
                aria-expanded={showInfo}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}
          </div>
          <p className="mt-2 text-xl sm:text-2xl font-bold text-foreground">{displayValue}</p>
        </div>
        <div className={`p-2 rounded-lg ${getCategoryColor(metric.category)} shrink-0`}>
          {getIcon(metric.category)}
        </div>
      </div>

      {/* Status indicator for metrics with known benchmarks */}
      {status && (
        <div className="mt-2 flex items-center gap-1.5 text-xs">
          <span
            className={`w-2 h-2 rounded-full ${
              status.status === 'good' ? 'bg-green-500' :
              status.status === 'ok' ? 'bg-yellow-500' :
              'bg-red-500'
            }`}
            aria-hidden="true"
          />
          <span className={`${
            status.status === 'good' ? 'text-green-400' :
            status.status === 'ok' ? 'text-yellow-400' :
            'text-red-400'
          }`}>
            {status.label}
          </span>
          <span className="text-muted-foreground">(ASHRAE 90.1: ≤300 hrs)</span>
        </div>
      )}

      {/* Trend indicator */}
      {metric.trend && (
        <div className="mt-3 flex items-center text-sm">
          <span
            className={`inline-flex items-center ${
              metric.trend.direction === 'up' ? 'text-red-400' :
              metric.trend.direction === 'down' ? 'text-green-400' :
              'text-muted-foreground'
            }`}
          >
            {metric.trend.direction === 'up' ? (
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            ) : metric.trend.direction === 'down' ? (
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            ) : null}
            {metric.trend.percentage.toFixed(1)}%
          </span>
          <span className="ml-2 text-muted-foreground">{metric.trend.label}</span>
        </div>
      )}

      {/* Contextual information popover */}
      {showInfo && context && (
        <div className="absolute left-0 right-0 top-full mt-2 z-10 bg-card border border-border rounded-lg shadow-lg p-3 text-sm">
          <p className="text-foreground">{context.description}</p>
          {context.benchmark && (
            <p className="mt-2 text-muted-foreground text-xs">
              <span className="font-medium">Benchmark:</span> {context.benchmark}
            </p>
          )}
          <button
            onClick={() => setShowInfo(false)}
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Props for SummaryCards component
 */
interface SummaryCardsProps {
  /** Parsed ESO data */
  data: ESOData;
  /** Building floor area in m² (optional, for EUI calculation) */
  buildingArea?: number;
  /** Unit system (SI or IP) */
  unitSystem?: UnitSystem;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Summary cards grid component
 */
export function SummaryCards({
  data,
  buildingArea = 0,
  unitSystem = 'SI',
  className = '',
}: SummaryCardsProps) {
  const metrics = useMemo(
    () => generateSummaryMetrics(data, buildingArea),
    [data, buildingArea]
  );

  if (metrics.length === 0) {
    return (
      <div className={`text-center text-muted-foreground py-8 ${className}`}>
        <svg className="w-10 h-10 mx-auto mb-2 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
        No summary metrics available
      </div>
    );
  }

  return (
    <section aria-label="Simulation summary metrics" className={className}>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
        {metrics.map((metric) => (
          <SummaryCard key={metric.title} metric={metric} unitSystem={unitSystem} />
        ))}
      </div>
    </section>
  );
}

export default SummaryCards;
